import { Search, ArrowLeftRight, Menu } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Avatar } from '../components/Avatar';
import { ThemeToggle } from './ThemeToggle';
import { BusinessSwitcher } from './BusinessSwitcher';
import { NotificationsBell } from './NotificationsBell';
import { useSession } from '../stores/session';

/**
 * Sticky app top bar.
 * Mobile (<lg): shows hamburger + business switcher + avatar.
 * Desktop: business switcher + global search (center) + actions (right).
 */
export function TopBar({ onOpenMobileNav }: { onOpenMobileNav?: () => void }) {
  const user = useSession((s) => s.user);
  return (
    <header
      className="sticky top-0 z-40 h-14 border-b border-zinc-200 bg-white/80 backdrop-blur-md
                 dark:bg-zinc-950/80 dark:border-zinc-700"
    >
      <div className="h-full px-4 sm:px-5 flex items-center gap-3">
        {/* Mobile: hamburger to open sidebar drawer */}
        {onOpenMobileNav && (
          <button
            type="button"
            onClick={onOpenMobileNav}
            className="lg:hidden -ml-1 w-9 h-9 grid place-items-center rounded-md text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Business switcher (top-left) */}
        <BusinessSwitcher />

        {/* Global search (center, hidden on small screens) */}
        <div className="hidden md:flex flex-1 max-w-md mx-auto">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            aria-label="Open command palette"
            className="w-full inline-flex items-center gap-2 px-3 py-1.5 rounded-md
                       text-sm text-zinc-500 dark:text-zinc-400
                       bg-zinc-50 dark:bg-zinc-800/40
                       border border-zinc-200 dark:border-zinc-700
                       hover:border-zinc-300 dark:hover:border-zinc-700
                       transition"
          >
            <Search className="w-4 h-4" />
            <span>Search for anything</span>
            <kbd
              className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium
                         bg-white dark:bg-zinc-800
                         text-zinc-500 dark:text-zinc-400
                         border border-zinc-200 dark:border-zinc-700"
            >
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium
                       text-zinc-700 dark:text-zinc-200
                       hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
          >
            <ArrowLeftRight className="w-4 h-4" /> Move money
          </button>
          <ThemeToggle />
          <NotificationsBell />
          <Link to="/profile" className="ml-1" aria-label="Account menu">
            <Avatar name={user?.name ?? ''} size="md" />
          </Link>
        </div>
      </div>
    </header>
  );
}
